/**
 * @author NikolaV
 */
import { ILayerWrapperOptions, LayerWrapper } from '../LayerWrapper';
import { latLonToEN, panTo } from '../ol-utils';
import { VehiclesStore } from '../../vehicles/VehiclesStore';
import { VehiclesFeatureFactory } from './VehiclesFeatureFactory';
import { autorun, reaction } from 'mobx';
import { Events } from '@shared/Events';

import { Point as OlPoint } from 'ol/geom';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import OlHeatmap from 'ol/layer/Heatmap';
import Feature, { FeatureLike } from 'ol/Feature';


export class VehiclesLayer extends LayerWrapper<IVehicle> {
    featureType: FeatureType = 'vehicle';
    showingTailForVehicleId = null;

    trailLinesLayer = new VectorLayer({
        source: new VectorSource(),
        style: (feature: FeatureLike) => {
            const isHover = feature.get('hover');
            return isHover ? VehiclesFeatureFactory.styles.tailHover : VehiclesFeatureFactory.styles.tail;
        },
    });
    trailHeatmapLayer = new OlHeatmap({
        source: new VectorSource({
            features: [
                new Feature({
                    geometry: new OlPoint([0, 0]),
                }),
            ],
        }),
        weight: () => 1,
    });

    constructor(opts: ILayerWrapperOptions<IVehicle>, readonly store: VehiclesStore) {
        super(opts);

        if (kuura.hasPrivilege('VEHICLE_PAGE')) {
            this.trailHeatmapLayer.setVisible(false);
            this.trailLinesLayer.setVisible(true);

            autorun(
                () => {
                    // only draw vehicle on active project, ref IN-1017
                    let onCurrentProject = [];

                    if (store.showVehiclesOnMap) {
                        onCurrentProject = store.vehiclesFilteredAndSorted
                            .filter(v => v.activeProject && v.activeProject.id == kuura.getProjectId())
                            .filter(v => v.position && v.position.latitude && v.position.longitude)
                            .sort((a, b) => (a.reportAlert === b.reportAlert ? 0 : a.reportAlert ? -1 : 1));
                    }

                    this.layerSource.clear(true);
                    this.drawAll(onCurrentProject);
                    // dont use setVisible on layer because extent will be wrong
                },
                { delay: 50 }
            );

            autorun(
                () => {
                    this.updatePositions(this.store.vehicles);
                },
                { delay: 50 }
            );

            reaction(
                () => store.isHeatmapActive,
                isHeatmapActive => {
                    if (isHeatmapActive) {
                        this.trailHeatmapLayer.setVisible(true);
                        this.trailLinesLayer.setVisible(false);
                    } else {
                        this.trailLinesLayer.setVisible(true);
                        this.trailHeatmapLayer.setVisible(false);
                    }
                }
            );
        }
    }

    clear() {
        this.layerSource.clear(true);
        this.clearPositionTrail();
    }

    private trailLinesLayerAdded = false;

    drawPositionTrail(vehicleId: number, from?: number, to?: number) {
        return VehiclesFeatureFactory.fetchAndMakeTailFeature(vehicleId, from, to).then(tailFeatureResult => {
            this.clearPositionTrail();
            this.showingTailForVehicleId = vehicleId;

            if (!this.trailLinesLayerAdded) {
                this.map.addLayer(this.trailLinesLayer);
                this.trailLinesLayer.setZIndex(this.layer.getZIndex() - 1);

                this.map.addLayer(this.trailHeatmapLayer);

                this.trailLinesLayerAdded = true;
            }

            if (tailFeatureResult.features && tailFeatureResult.features.length > 0) {
                const mouseInteraction = new VehiclesFeatureFactory.VehicleTrailDesktopMouseInteraction(this.map);
                tailFeatureResult.features.forEach(lineFeature => {
                    lineFeature.set('mouseInteraction', mouseInteraction);
                    (this.trailLinesLayer.getSource() as VectorSource).addFeature(lineFeature);
                });
            }

            if (!!tailFeatureResult.routes) {
                tailFeatureResult.routes?.forEach(route =>
                    route.forEach(p => {
                        (this.trailHeatmapLayer.getSource() as VectorSource).addFeature(
                            new Feature({
                                geometry: new OlPoint(latLonToEN(p.latitude, p.longitude)),
                            })
                        );
                    })
                );
            }

            return tailFeatureResult;
        });
    }

    clearPositionTrail() {
        (this.trailHeatmapLayer.getSource() as VectorSource).clear(true);
        (this.trailLinesLayer.getSource() as VectorSource).clear(true);
        this.showingTailForVehicleId = null;
    }

    fetchAndDrawFolder(): JQueryPromise<IVehicle[]> {
        this.store.startInterval();
        return this.store.fetchAll();
    }

    updatePositions(vehicles: IVehicle[]) {
        Events.trigger('map-vehicles-updated', { vehicles });

        vehicles.forEach(v => {
            const feature = this.getFeatureById(v.id);
            const data = this.getAjaxData(feature);
            if (!feature) return;

            const coord: Coordinate = latLonToEN(v.position.latitude, v.position.longitude);
            if (!coord) {
                console.warn('Vehicle position coordinates invalid', v);
                return;
            }
            feature.setGeometry(new OlPoint(coord));

            if (data.iconDataUrl != v.iconDataUrl) {
                $.getJSON(v.iconDataUrl, ir => {
                    data.iconDataUrl = v.iconDataUrl;
                    data.iconImage = ir.url;
                    this.layerSource.dispatchEvent('change');
                });
            }
        });
    }

    panToVehicle(idOrUuid: number | string) {
        const vehicle = typeof idOrUuid === 'number' ? this.store.getVehicleById(idOrUuid) : this.store.getVehicleByUuid(idOrUuid);
        if (!vehicle) return;
        if (!vehicle.position) return;

        const coord = latLonToEN(vehicle.position.latitude, vehicle.position.longitude);
        if (coord) panTo(this.map, coord[0], coord[1]);
    }

    setTrailOpacity = (opacity: number /*1-100*/) => {
        this.store.trailOpacity = opacity;
        this.store.isHeatmapActive ? this.trailHeatmapLayer.setOpacity(opacity / 100) : this.trailLinesLayer.setOpacity(opacity / 100);
    };

    changeVisibleLayer = () => {
        this.store.isHeatmapActive = !this.store.isHeatmapActive;
    };
}
